import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { Group, Layout, Page, Tab } from "@/comn/components";
import { useFetch, useForm, useModal, useToast, useTab, useResource, usePage } from "@/comn/hooks";
import {
    APIS,
    SF_ORG_USR_INFO,
    URLS,
    CSTM_OFCE_CD_RQRD_ARR,
} from "@/tra/tancis/ptli/usr/orgusr/services/PtliOrgUsrService";

/*
 * registration for Organization User Management
 * !== 기관 사용자 관리 등록 ==!
 */
export const PTLI0917002S = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!

    useResource({
        defaultSchema: [{ area: "orgDeptCd" }, { area: "orgCd" }, { area: "cntyCd" }, { area: "cstmCd" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Organization Information Form !== 기관 정보 form ==!
        usrInfo: useForm({
            defaultSchema: SF_ORG_USR_INFO,
            defaultValues: {},
        }),
    };

    const psnTin = form.usrInfo.watch("psnTin");

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Create Organization User Information !== 기관 사용자 정보 등록 ==!
        createOrgUsrInfo: useFetch({
            api: (data: any) => APIS.createOrgUsrInfo(data),
            enabled: false,
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00058",
                    onConfirm: () => navigate(`${URLS.ptli0917001q}?menuId=${menuId}`),
                    close: false,
                }); // Successfully inserted. !== 정상적으로 등록되었습니다. ==!
            },
            onError: (err) => {
                toast.showToast({ type: "warning", content: err.response.data.errorMessage }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
            },
        }),

        getPayerTinNoLst: useFetch({
            api: () => {
                return APIS.getPayerTinNoLst(psnTin, "N");
            },
            // enabled: !!coTin,
            // key: [coTin],
            onSuccess: (data) => {
                const tinNoList = data.tinNoList.content;
                if (tinNoList.length == 1) {
                    const tinNoInfo = tinNoList[0];

                    const tinNo = tinNoInfo.tinNo;
                    form.usrInfo.setValue("psnTin", psnTin);
                    form.usrInfo.setValue("usrNm", tinNoInfo.coNm);
                    form.usrInfo.setValue("usrMoblNo", tinNoInfo.rprsTelno);
                    form.usrInfo.setValue("usrEmlAddr", tinNoInfo.rprsEml);
                    form.usrInfo.setValue("rsdtAddr", tinNoInfo.coAddr);
                    form.usrInfo.clearErrors("psnTin");

                    if (tinNo) {
                        fetch.getUsrDuplCheck.fetch(tinNoInfo.tinNo);
                    } else {
                        form.usrInfo.clearErrors("psnTin");
                    }
                } else {
                    toast.showToast({ type: "error", content: "msg.clr.00004" });
                }
            },
            onError: (err) => {
                toast.showToast({ type: "warning", content: err.response.data.errorMessage }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
            },
        }),

        getUsrDuplCheck: useFetch({
            api: (data: any) => APIS.getUsrDuplCheck(data),
            enabled: false,
            onSuccess: (data) => {
                console.log(data);
                if (!data.usrDuplCheck.content) {
                    form.usrInfo.clearErrors("psnTin");
                } else {
                    form.usrInfo.setError("psnTin", {
                        message: "msg.ptl.00770", // Please check your TIN. !== TIN을 조회해주세요. ==!
                        type: "error",
                    });

                    form.usrInfo.setValue("psnTin", null);
                    form.usrInfo.setValue("usrNm", null);
                    form.usrInfo.setValue("usrMoblNo", null);
                    form.usrInfo.setValue("usrEmlAddr", null);
                    form.usrInfo.setValue("rsdtAddr", null);
                }
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click List Button !== 목록 버튼 클릭 ==!
        onListBtnClick: () => {
            navigate(`${URLS.ptli0917001q}?menuId=${menuId}`);
        },
        // Click Organization Save Button !== 기관 저장 버튼 클릭 ==!
        onOrgSaveBtnClick: () => {
            if (CSTM_OFCE_CD_RQRD_ARR.includes(form.usrInfo.getValue("orgCd"))) {
                if (comnUtils.isEmpty(form.usrInfo.getValue("cstmOfceCd"))) {
                    form.usrInfo.setError("cstmOfceCd", {
                        message: "msg.com.00005", // Mandatory fields !== 필수 입력 필드 ==!
                        type: "error",
                    });
                    return;
                }
            }

            if (!comnUtils.isEmpty(form.usrInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }
            // save Information of Organization !== 기관 정보 저장 ==!
            form.usrInfo.handleSubmit(
                () => {
                    const data = form.usrInfo.getValues();
                    modal.openModal({
                        content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                        onConfirm: () => {
                            fetch.createOrgUsrInfo.fetch(data);
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },

        setCoTinChange: () => {
            // const psnTin = form.usrInfo.getValue("psnTin");
            form.usrInfo.setError("psnTin", {
                message: "msg.ptl.00755", // Please check your TIN. !== TIN을 조회해주세요. ==!
                type: "error",
            });
        },

        checkPwd: () => {
            const usrPwd = form.usrInfo.getValue("usrPwd");
            const usrPwdCfrm = form.usrInfo.getValue("usrPwdCfrm");

            if (!comnUtils.isEmpty(usrPwd) && !comnUtils.equals(usrPwd, usrPwdCfrm)) {
                form.usrInfo.setError("usrPwdCfrm", {
                    message: "msg.ptl.00712", // Passwords do not match. !== 비밀번호가 일치하지 않습니다. ==!
                    type: "error",
                });
            } else {
                form.usrInfo.clearErrors("usrPwdCfrm");
            }
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {}, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control
                                {...form.usrInfo.schema.psnTin}
                                rightButton={{
                                    icon: "search",
                                    onClick: () => {
                                        const usrNo = form.usrInfo.getValue("psnTin");
                                        if (usrNo?.length == 9) {
                                            form.usrInfo.clearErrors("psnTin");
                                            fetch.getPayerTinNoLst.fetch();
                                        } else {
                                            form.usrInfo.setError("psnTin", {
                                                message: "msg.esw.00070", // TIN must be 9 digits. !== TIN은 9자리여야 합니다. ==!
                                                type: "error",
                                            });
                                        }
                                    },
                                }}
                                onChange={() => {
                                    handler.setCoTinChange();
                                }}
                                maxLength={9}
                            />
                            <Group.Control {...form.usrInfo.schema.usrNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.usrInfo.schema.orgCd}
                                onChange={(event) => {
                                    if (CSTM_OFCE_CD_RQRD_ARR.includes(event)) {
                                        if (comnUtils.isEmpty(form.usrInfo.getValue("cstmOfceCd"))) {
                                            form.usrInfo.setError("cstmOfceCd", {
                                                message: "msg.com.00005", // Mandatory fields !== 필수 입력 필드 ==!
                                                type: "error",
                                            });
                                        }
                                    } else {
                                        form.usrInfo.clearErrors("cstmOfceCd");
                                    }
                                }}
                            />
                            <Group.Control {...form.usrInfo.schema.deptCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.usrInfo.schema.usrPwd} />
                            <Group.Control
                                {...form.usrInfo.schema.usrPwdCfrm}
                                onBlur={() => {
                                    handler.checkPwd();
                                }}
                            />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.usrInfo.schema.frgnrYn} />
                            <Group.Control {...form.usrInfo.schema.aplcCntyCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.usrInfo.schema.usrMoblNo} />
                            <Group.Control {...form.usrInfo.schema.usrEmlAddr} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.usrInfo.schema.rsdtAddr} />
                            <Group.Control {...form.usrInfo.schema.orgRppnYn} defaultValue={"N"} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.usrInfo.schema.cstmOfceCd}
                                callback={(event) => {
                                    form.usrInfo.clearErrors("cstmOfceCd");
                                }}
                            />
                            <Group.Cell size={6}>{t("msg.ptl.00799")}</Group.Cell>
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout direction="row">
                <Layout.Left>
                    <Layout.Button role={"list"} onClick={handler.onListBtnClick} />
                </Layout.Left>
                <Layout.Right>
                    <Layout.Button role={"save"} onClick={handler.onOrgSaveBtnClick} />
                </Layout.Right>
            </Layout>
        </Page>
    );
};
